import { IPromise } from "angular";

import { ListTypes, ILoadListV2 } from "../models/models";
import { ICollection } from "@gtmhub/models";
import { IRestLayerRequest } from "@gtmhub/core";
import { GoalService } from "@gtmhub/goals/goal.service";
import { IMetric, MetricService } from "@gtmhub/okrs/metrics";
import { ITask, TaskService } from "@gtmhub/tasks";
import { EmployeeService, IEmployee } from "@gtmhub/employees";
import { ITeam, TeamService } from "@gtmhub/teams";
import { projectionWithoutRequiredColumns, projectionWithRequiredColumns } from "../controllers/helpers";
import { IGridFilter, INumericGridFilter, INumericRange, RestLayerFilterFactory, toRestLayerRequest } from "@gtmhub/shared/components/grid-filtering-bar";
import {
  And,
  Equals,
  Greater,
  GreaterOrEqual,
  Less,
  LessOrEqual,
  listFilterOverrides,
  listProjectionOverrides,
  listSortingOverrides,
  Or,
  RestFilterExpression,
} from "../filter-utils";
import { IGoal } from "@gtmhub/goals/models";
import { IConfidenceSettingsRootScopeService } from "@gtmhub/configuration/controllers/confidence-settings";

export class ListQueryService {
  public static $inject = ["GoalService", "MetricService", "TaskService", "EmployeeService", "TeamService", "$rootScope"];

  constructor(
    private goalService: GoalService,
    private metricService: MetricService,
    private tasksService: TaskService,
    private employeeService: EmployeeService,
    private teamService: TeamService,
    private $rootScope: IConfidenceSettingsRootScopeService
  ) {}

  // The type of ICollection can be goals, metrics, tasks, employees, teams
  public loadItems(requestParams: ILoadListV2): IPromise<ICollection<IGoal>>;
  public loadItems(requestParams: ILoadListV2): IPromise<ICollection<IMetric>>;
  public loadItems(requestParams: ILoadListV2): IPromise<ICollection<ITask>>;
  public loadItems(requestParams: ILoadListV2): IPromise<ICollection<IEmployee>>;
  public loadItems(requestParams: ILoadListV2): IPromise<ICollection<ITeam>>;
  public loadItems(requestParams: ILoadListV2): IPromise<ICollection<unknown>> {
    const req = this.constructLoadListV2Request(requestParams);
    const listType = requestParams.listType;
    switch (listType) {
      case ListTypes.Objective:
        return this.goalService.getGoalsV2(req);
      case ListTypes["Key Result"]:
        return this.metricService.getMetricsV2(req);
      case ListTypes.Task:
        return this.tasksService.getTasksV2(req);
      case ListTypes.Employee:
        return this.employeeService.getEmployeesV2(req);
      case ListTypes.Team:
        return this.teamService.getTeams(req);
    }
  }

  constructLoadListV2Request(params: ILoadListV2): IRestLayerRequest {
    const listType = params.listType;
    const filterOverrides = this.constructFilterOverrides(listType);
    const projectionOverrides = listProjectionOverrides.get(listType);
    const sortOverrides = listSortingOverrides.get(listType);

    const request = toRestLayerRequest({
      projectionOverride: projectionOverrides,
      sortingOverride: sortOverrides,
      filtersOverride: filterOverrides,
      filters: params.filters,
      sorting: params.sorting,
      projection: params.includeRequiredColumns ? projectionWithRequiredColumns(listType, params.projections) : projectionWithoutRequiredColumns(params.projections),
    });
    request.skip = params.skip;
    request.limit = params.limit;
    return request;
  }

  private constructFilterOverrides(listType: ListTypes): Record<string, RestLayerFilterFactory> {
    const filterOverrides = listFilterOverrides.get(listType) || {};

    const { confidenceType, confidenceMapping } = this.$rootScope.confidenceSettings;
    if (confidenceType && confidenceType !== "numeric") {
      const field = "confidence.value";

      filterOverrides[field] = (filter: IGridFilter) => {
        const hasZeroRange = confidenceMapping.some((m) => m.range.from === 0 && m.range.to === 0);
        const correspondingConfidenceMapping = confidenceMapping.find((m) => m.name === filter.value);
        const { from, to } = correspondingConfidenceMapping.range;
        const filterCopy: IGridFilter = {
          fieldName: field,
          value: { from: from, to: to },
          dataType: "numeric",
          operator: filter.operator,
        };
        const args = { filter: filterCopy, fieldNameOverride: field, hasZeroRange: hasZeroRange };
        return this.constructConfidenceLevelRestLayerExpression(args).toRestLayerFilter();
      };
    }

    return filterOverrides;
  }

  private constructConfidenceLevelRestLayerExpression(args: { filter: INumericGridFilter; fieldNameOverride: string; hasZeroRange: boolean }): RestFilterExpression {
    const rangeValue = args.filter.value as INumericRange;

    switch (args.filter.operator) {
      case "equals":
        if (rangeValue.to === 0) {
          return new Equals(args.fieldNameOverride, 0);
        } else if (!args.hasZeroRange && rangeValue.from === 0) {
          return new And([new GreaterOrEqual(args.fieldNameOverride, rangeValue.from), new LessOrEqual(args.fieldNameOverride, rangeValue.to)]);
        }
        return new And([new Greater(args.fieldNameOverride, rangeValue.from), new LessOrEqual(args.fieldNameOverride, rangeValue.to)]);

      case "notEqual":
        if (rangeValue.to === 0 || (!args.hasZeroRange && rangeValue.from === 0)) {
          return new Greater(args.fieldNameOverride, rangeValue.to);
        }
        return new Or([new LessOrEqual(args.fieldNameOverride, rangeValue.from), new Greater(args.fieldNameOverride, rangeValue.to)]);

      case "greater":
        return new Greater(args.fieldNameOverride, rangeValue.to);

      case "less":
        if (rangeValue.to === 0 || (!args.hasZeroRange && rangeValue.from === 0)) {
          return new Less(args.fieldNameOverride, 0);
        }
        return new LessOrEqual(args.fieldNameOverride, rangeValue.from);
    }
  }
}
